import { useCallback, useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { Ban, CheckCheck, CheckSquare, ExternalLink, FileText, Scale, Sparkles, Undo2, Users } from 'lucide-react'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { NativeSelect } from '@/components/ui/native-select'
import { Separator } from '@/components/ui/separator'
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from '@/components/ui/sheet'
import { Skeleton } from '@/components/ui/skeleton'
import { PublicationDeadlinePanel } from '@/components/deadlines/PublicationDeadlinePanel'
import { useTaskDrawer } from '@/contexts/TaskDrawerContext'
import { conferenceActionFor, formatDateBr, INBOX_STATES, priorityLabel, publicationPlainText } from '@/lib/publication-inbox'
import { groupPartiesByPole } from '@/lib/publication-parties'
import type { InboxState } from '@/services/publication-inbox-service'
import { PublicationService } from '@/services/publicationService'
import { apiErrorMessage } from '@/services/team-service'
import type { DJENPublication, PublicationPriority } from '@/types/publication'
import * as toast from '@/lib/toast'
import { CreateFromPublicationWizard } from '../CreateFromPublicationWizard'
import { LinkCaseButton } from '../LinkCaseButton'
import { prefillFromPublication } from '../task-prefill'
import type { ConferenceAction } from './useInboxConference'
import { PublicationNotesPanel } from './PublicationNotesPanel'

const PRIORITIES: Array<{ value: PublicationPriority; label: string }> = (['LOW', 'MEDIUM', 'HIGH', 'URGENT'] as const)
  .map((value) => ({ value, label: priorityLabel(value) }))

interface PublicationFullSheetProps {
  /** Publicação aberta; null fecha a ficha. */
  publicationId: number | null
  /** Estado na caixa de entrada (quando aberta por ela): define o selo e Conferida/Desfazer. */
  estado?: InboxState
  onClose: () => void
  /** Algo mudou (nota, prioridade, vínculo, tarefa, sem providência): a lista recarrega. */
  onChanged: () => void
  onConference?: (action: ConferenceAction) => void
  conferenceBusy?: boolean
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <>
      <dt className="text-muted-foreground">{label}</dt>
      <dd className="min-w-0 break-words">{children || '—'}</dd>
    </>
  )
}

function Section({ title, icon: Icon, children }: { title: string; icon: typeof FileText; children: React.ReactNode }) {
  return (
    <section className="flex flex-col gap-2">
      <h3 className="flex items-center gap-1.5 text-sm font-semibold">
        <Icon className="size-4 text-muted-foreground" aria-hidden="true" /> {title}
      </h3>
      {children}
    </section>
  )
}

/**
 * Ficha completa da publicação (gaveta larga): texto integral, destinatários,
 * processo vinculado, prazo, notas e as ações da caixa de entrada.
 */
export function PublicationFullSheet({ publicationId, estado, onClose, onChanged, onConference, conferenceBusy }: PublicationFullSheetProps) {
  const taskDrawer = useTaskDrawer()
  const [publication, setPublication] = useState<DJENPublication | null>(null)
  const [busy, setBusy] = useState(false)
  const [showWizard, setShowWizard] = useState(false)

  const load = useCallback(async (id: number) => {
    try {
      const response = await PublicationService.getPublicationById(id)
      setPublication(response.data)
    } catch (err) {
      toast.error(apiErrorMessage(err, 'Não foi possível abrir a publicação'))
      onClose()
    }
  }, [onClose])

  useEffect(() => {
    setPublication(null)
    setShowWizard(false)
    if (publicationId != null) load(publicationId)
  }, [publicationId, load])

  const reload = async () => {
    if (publicationId != null) await load(publicationId)
    onChanged()
  }

  const createTask = async (current: DJENPublication) => {
    const prefill = await prefillFromPublication(current)
    onClose()
    taskDrawer.newTask(prefill, onChanged)
  }

  const semProvidencia = async (current: DJENPublication) => {
    setBusy(true)
    try {
      await PublicationService.markAsIrrelevant(current.id, 'Sem providência (ficha completa)')
      toast.success('Publicação marcada como sem providência')
      onChanged()
      onClose()
    } catch (err) {
      toast.error(apiErrorMessage(err, 'Não foi possível atualizar a publicação'))
    } finally {
      setBusy(false)
    }
  }

  const changePriority = async (current: DJENPublication, priority: PublicationPriority) => {
    setBusy(true)
    try {
      await PublicationService.setPriority(current.id, priority)
      toast.success('Prioridade atualizada')
      await reload()
    } catch (err) {
      toast.error(apiErrorMessage(err, 'Não foi possível atualizar a prioridade'))
    } finally {
      setBusy(false)
    }
  }

  const stateLabel = estado ? (estado === 'CONFERIDA' ? 'Conferida' : INBOX_STATES.find((s) => s.value === estado)?.label) : null
  const conference = estado && onConference ? conferenceActionFor(estado) : null
  const priority = publication ? publication.manualPriority ?? publication.priority ?? '' : ''
  const parties = groupPartiesByPole(publication?.destinatarios)
  const lawyers = publication?.destinatarioAdvogados ?? []
  const numero = publication ? publication.numeroProcessoComMascara || publication.numeroProcesso : ''

  return (
    <Sheet open={publicationId != null} onOpenChange={(open) => !open && onClose()}>
      <SheetContent side="right" className="overflow-y-auto data-[side=right]:w-full data-[side=right]:sm:max-w-4xl">
        {!publication ? (
          <div className="flex flex-col gap-3 p-4" aria-busy="true">
            <SheetHeader className="p-0">
              <SheetTitle>Carregando publicação…</SheetTitle>
              <SheetDescription className="sr-only">Ficha completa da publicação</SheetDescription>
            </SheetHeader>
            <Skeleton className="h-6 w-2/3" />
            <Skeleton className="h-64" />
          </div>
        ) : (
          <>
            <SheetHeader className="pr-12">
              <SheetTitle className="text-lg tabular-nums">{numero}</SheetTitle>
              <SheetDescription>
                {[publication.tipoComunicacao, publication.siglaTribunal, publication.nomeOrgao].filter(Boolean).join(' · ')}
              </SheetDescription>
              <div className="flex flex-wrap gap-1.5 pt-1">
                {stateLabel
                  ? <Badge>{stateLabel}</Badge>
                  : <Badge variant="outline">{PublicationService.getStatusLabel(publication.analysisStatus)}</Badge>}
                {publication.nomeClasse && <Badge variant="outline">{publication.nomeClasse}</Badge>}
                {priority && <Badge variant="secondary">Prioridade {priorityLabel(priority)}</Badge>}
                {publication.dataCancelamento && <Badge variant="destructive">Cancelada</Badge>}
              </div>
            </SheetHeader>

            <div className="flex flex-col gap-4 px-4 pb-6">
              <div className="flex flex-wrap gap-2">
                {conference === 'conferir' && (
                  <Button disabled={conferenceBusy} onClick={() => onConference?.('conferir')}>
                    <CheckCheck aria-hidden="true" /> Conferida
                  </Button>
                )}
                {conference === 'desconferir' && (
                  <Button variant="outline" disabled={conferenceBusy} onClick={() => onConference?.('desconferir')}>
                    <Undo2 aria-hidden="true" /> Desfazer
                  </Button>
                )}
                <Button variant={conference === 'conferir' ? 'outline' : 'default'} onClick={() => createTask(publication)}>
                  <CheckSquare aria-hidden="true" /> Criar tarefa
                </Button>
                <Button variant="outline" onClick={() => setShowWizard(true)}>
                  <Sparkles aria-hidden="true" /> Criar processo/evento
                </Button>
                {publication.analysisStatus !== 'ARCHIVED' && (
                  <Button variant="outline" disabled={busy} onClick={() => semProvidencia(publication)}>
                    <Ban aria-hidden="true" /> Sem providência
                  </Button>
                )}
                {publication.link && (
                  <Button variant="ghost" asChild>
                    <a href={publication.link} target="_blank" rel="noreferrer">
                      Ver no DJEN <ExternalLink aria-hidden="true" />
                    </a>
                  </Button>
                )}
              </div>

              {publication.dataCancelamento && (
                <p role="note" className="rounded-md border border-destructive/30 bg-destructive/5 p-2 text-sm text-destructive">
                  Comunicação cancelada em {formatDateBr(publication.dataCancelamento)}
                  {publication.motivoCancelamento ? `: ${publication.motivoCancelamento}` : ''}
                </p>
              )}

              <div className="grid grid-cols-1 gap-6 lg:grid-cols-[minmax(0,1fr)_17rem]">
                <Section title="Texto da publicação" icon={FileText}>
                  <div className="max-w-prose whitespace-pre-wrap rounded-md border bg-muted/30 p-4 text-[0.95rem] leading-7">
                    {publicationPlainText(publication.texto)}
                  </div>
                </Section>

                <aside className="flex flex-col gap-5">
                  <Section title="Dados" icon={Scale}>
                    <dl className="grid grid-cols-[auto_1fr] gap-x-3 gap-y-1 text-sm">
                      <Field label="Disponibilizada">{formatDateBr(publication.dataDisponibilizacao)}</Field>
                      <Field label="Importada">{formatDateBr(publication.createdAt)}</Field>
                      <Field label="Tribunal">{publication.siglaTribunal}</Field>
                      <Field label="Órgão">{publication.nomeOrgao}</Field>
                      <Field label="Tipo">{publication.tipoDocumento}</Field>
                      <Field label="Meio">{publication.meioCompleto}</Field>
                      {publication.numeroComunicacao ? <Field label="Comunicação">{publication.numeroComunicacao}</Field> : null}
                    </dl>
                  </Section>

                  <Separator />

                  <Section title="Processo vinculado" icon={FileText}>
                    {publication.linkedCaseId ? (
                      <p className="text-sm">
                        <Link className="font-medium text-primary hover:underline" to={`/cases/${publication.linkedCaseId}`} onClick={onClose}>
                          {publication.caseInternalNumber || 'Abrir processo'}
                        </Link>
                        {publication.autoDecided && <span className="text-muted-foreground"> · vinculada automaticamente</span>}
                      </p>
                    ) : (
                      <>
                        <p className="text-sm text-muted-foreground">Não vinculada a processo do escritório.</p>
                        <LinkCaseButton publication={publication} onLinked={reload} />
                      </>
                    )}
                  </Section>

                  <div className="flex flex-col gap-1.5">
                    <label htmlFor="publication-priority" className="text-sm font-semibold">Prioridade</label>
                    <NativeSelect
                      id="publication-priority"
                      value={priority}
                      disabled={busy}
                      onChange={(e) => e.target.value && changePriority(publication, e.target.value as PublicationPriority)}
                    >
                      {!priority && <option value="">Sem prioridade</option>}
                      {PRIORITIES.map((p) => <option key={p.value} value={p.value}>{p.label}</option>)}
                    </NativeSelect>
                  </div>

                  <Separator />

                  <Section title="Destinatários" icon={Users}>
                    {([['ativo', 'Polo ativo'], ['passivo', 'Polo passivo'], ['outros', 'Outros']] as const).map(([key, label]) =>
                      parties[key].length > 0 && (
                        <div key={key} className="flex flex-col gap-0.5 text-sm">
                          <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">{label}</p>
                          <ul>
                            {parties[key].map((d, i) => (
                              <li key={`${key}-${i}`}>
                                {d.nome}
                                {key === 'outros' && d.polo ? <span className="text-muted-foreground"> ({d.polo})</span> : null}
                              </li>
                            ))}
                          </ul>
                        </div>
                      )
                    )}
                    {lawyers.length > 0 && (
                      <div className="flex flex-col gap-0.5 text-sm">
                        <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">Advogados</p>
                        <ul>
                          {lawyers.map((a) => (
                            <li key={a.id}>
                              {a.advogado?.nome || 'Nome não disponível'}
                              <span className="text-muted-foreground"> · OAB/{a.advogado?.ufOab || '—'} {a.advogado?.numeroOab || '—'}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                    {!publication.destinatarios?.length && lawyers.length === 0 && (
                      <p className="text-sm text-muted-foreground">Sem destinatários informados.</p>
                    )}
                  </Section>
                </aside>
              </div>

              <Separator />
              <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
                <PublicationDeadlinePanel
                  publication={{
                    id: publication.id,
                    siglaTribunal: publication.siglaTribunal,
                    dataDisponibilizacao: publication.dataDisponibilizacao,
                    numeroProcesso: numero,
                    nomeOrgao: publication.nomeOrgao,
                  }}
                />
                <PublicationNotesPanel publicationId={publication.id} onAdded={onChanged} />
              </div>
            </div>

            <Dialog open={showWizard} onOpenChange={setShowWizard}>
              <DialogContent size="2xl" fullScreenOnMobile className="max-h-[90vh] overflow-y-auto max-sm:max-h-dvh">
                <DialogHeader>
                  <DialogTitle className="flex items-center gap-2">
                    <Sparkles className="size-5 text-muted-foreground" aria-hidden="true" /> Criar a partir da publicação
                  </DialogTitle>
                  <DialogDescription>A classificação automática do texto sugere processo, evento ou tarefa.</DialogDescription>
                </DialogHeader>
                <CreateFromPublicationWizard
                  publication={publication}
                  onSuccess={() => {
                    setShowWizard(false)
                    reload()
                  }}
                  onCancel={() => setShowWizard(false)}
                  onCreateTask={() => {
                    setShowWizard(false)
                    createTask(publication)
                  }}
                />
              </DialogContent>
            </Dialog>
          </>
        )}
      </SheetContent>
    </Sheet>
  )
}
